import { useState } from 'react';
import { type GoogleAdsPlacement, type AdSize } from '@/api/tenants/googleAdsPlacements';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronDown, ChevronRight, Plus, X, Trash2 } from 'lucide-react';
import { DEFAULT_TARGETING_KEYS } from './constants';

interface PlacementConfigFormProps {
  placement: GoogleAdsPlacement;
  label: string;
  description: string;
  onChange: (placement: GoogleAdsPlacement) => void;
  isSaving: boolean;
  defaultExpanded?: boolean;
  readOnlyTargetingKeys?: string[];
}

export function PlacementConfigForm({
  placement,
  label,
  description,
  onChange,
  isSaving,
  defaultExpanded = false,
  readOnlyTargetingKeys = [],
}: PlacementConfigFormProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const updateField = <K extends keyof GoogleAdsPlacement>(
    field: K,
    value: GoogleAdsPlacement[K]
  ) => {
    onChange({ ...placement, [field]: value });
  };

  const addSize = () => {
    const currentSizes = placement.sizes || [];
    updateField('sizes', [...currentSizes, [320, 50]]);
  };

  const updateSize = (index: number, width: number, height: number) => {
    const currentSizes = placement.sizes || [];
    const updated = [...currentSizes];
    updated[index] = [width, height];
    updateField('sizes', updated);
  };

  const removeSize = (index: number) => {
    const currentSizes = placement.sizes || [];
    updateField(
      'sizes',
      currentSizes.filter((_, i) => i !== index)
    );
  };

  const addSizeMapping = () => {
    const currentMappings = placement.sizeMappings || [];
    updateField('sizeMappings', [...currentMappings, { viewport: [0, 0], sizes: [] }]);
  };

  const updateSizeMapping = (index: number, viewport: [number, number], sizes: AdSize[]) => {
    const currentMappings = placement.sizeMappings || [];
    const updated = [...currentMappings];
    updated[index] = { viewport, sizes };
    updateField('sizeMappings', updated);
  };

  const removeSizeMapping = (index: number) => {
    const currentMappings = placement.sizeMappings || [];
    updateField(
      'sizeMappings',
      currentMappings.filter((_, i) => i !== index)
    );
  };

  const updateTargeting = (key: string, value: string) => {
    const currentTargeting = placement.targeting || {};
    updateField('targeting', { ...currentTargeting, [key]: value });
  };

  const removeTargetingKey = (key: string) => {
    if ((DEFAULT_TARGETING_KEYS as readonly string[]).includes(key)) return; // Can't remove default keys
    const currentTargeting = placement.targeting || {};
    const updated = { ...currentTargeting };
    delete updated[key];
    updateField('targeting', updated);
  };

  const addCustomTargetingKey = () => {
    const currentTargeting = placement.targeting || {};
    const newKey = `custom_${Object.keys(currentTargeting).length + 1}`;
    updateTargeting(newKey, '');
  };

  const targeting = placement.targeting || {};
  const customKeys = Object.keys(targeting).filter(
    k => !(DEFAULT_TARGETING_KEYS as readonly string[]).includes(k)
  );

  return (
    <div className="rounded-lg border">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3 flex-1">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <div className="flex-1">
            <div className="font-medium">{label}</div>
            <div className="text-sm text-muted-foreground">{description}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-muted-foreground">
            {placement.enabled ? 'Active' : 'Hidden'}
          </div>
          <Switch
            checked={placement.enabled}
            disabled={isSaving}
            onCheckedChange={checked => updateField('enabled', checked)}
            onClick={e => e.stopPropagation()}
          />
        </div>
      </div>

      {isExpanded && (
        <div className="border-t p-4 space-y-6 bg-muted/20">
          {/* Sizes */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Sizes</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addSize}
                disabled={isSaving}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Size
              </Button>
            </div>
            <div className="space-y-2">
              {(placement.sizes || []).map((size, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={size[0]}
                    onChange={e => updateSize(index, parseInt(e.target.value) || 0, size[1])}
                    placeholder="Width"
                    disabled={isSaving}
                    className="w-24"
                  />
                  <span className="text-muted-foreground">×</span>
                  <Input
                    type="number"
                    value={size[1]}
                    onChange={e => updateSize(index, size[0], parseInt(e.target.value) || 0)}
                    placeholder="Height"
                    disabled={isSaving}
                    className="w-24"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSize(index)}
                    disabled={isSaving}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {(!placement.sizes || placement.sizes.length === 0) && (
                <div className="text-sm text-muted-foreground py-2">No sizes configured</div>
              )}
            </div>
          </div>

          {/* Size Mappings */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Size Mappings (Responsive)</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addSizeMapping}
                disabled={isSaving}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Mapping
              </Button>
            </div>
            <div className="space-y-3">
              {(placement.sizeMappings || []).map((mapping, index) => (
                <div key={index} className="border rounded-lg p-3 space-y-2 bg-background">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Viewport</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSizeMapping(index)}
                      disabled={isSaving}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={mapping.viewport[0]}
                      onChange={e =>
                        updateSizeMapping(
                          index,
                          [parseInt(e.target.value) || 0, mapping.viewport[1]],
                          mapping.sizes
                        )
                      }
                      placeholder="Min Width"
                      disabled={isSaving}
                      className="w-24"
                    />
                    <span className="text-muted-foreground">×</span>
                    <Input
                      type="number"
                      value={mapping.viewport[1]}
                      onChange={e =>
                        updateSizeMapping(
                          index,
                          [mapping.viewport[0], parseInt(e.target.value) || 0],
                          mapping.sizes
                        )
                      }
                      placeholder="Min Height"
                      disabled={isSaving}
                      className="w-24"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs mr-2">Sizes for this viewport</Label>
                    {mapping.sizes.map((size, sizeIndex) => (
                      <div key={sizeIndex} className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={size[0]}
                          onChange={e => {
                            const updatedSizes = [...mapping.sizes];
                            updatedSizes[sizeIndex] = [parseInt(e.target.value) || 0, size[1]];
                            updateSizeMapping(index, mapping.viewport, updatedSizes);
                          }}
                          placeholder="Width"
                          disabled={isSaving}
                          className="w-20"
                        />
                        <span className="text-muted-foreground">×</span>
                        <Input
                          type="number"
                          value={size[1]}
                          onChange={e => {
                            const updatedSizes = [...mapping.sizes];
                            updatedSizes[sizeIndex] = [size[0], parseInt(e.target.value) || 0];
                            updateSizeMapping(index, mapping.viewport, updatedSizes);
                          }}
                          placeholder="Height"
                          disabled={isSaving}
                          className="w-20"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const updatedSizes = mapping.sizes.filter((_, i) => i !== sizeIndex);
                            updateSizeMapping(index, mapping.viewport, updatedSizes);
                          }}
                          disabled={isSaving}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const updatedSizes = [...mapping.sizes, [320, 50] as AdSize];
                        updateSizeMapping(index, mapping.viewport, updatedSizes);
                      }}
                      disabled={isSaving}
                      className="mt-1"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Size
                    </Button>
                  </div>
                </div>
              ))}
              {(!placement.sizeMappings || placement.sizeMappings.length === 0) && (
                <div className="text-sm text-muted-foreground py-2">
                  No size mappings configured
                </div>
              )}
            </div>
          </div>

          {/* Targeting */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Targeting</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCustomTargetingKey}
                disabled={isSaving}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Custom Key
              </Button>
            </div>
            <div className="space-y-2">
              {/* Default targeting keys */}
              {DEFAULT_TARGETING_KEYS.map(key => (
                <div key={key} className="flex items-center gap-2">
                  <Label className="w-20 text-xs">{key}</Label>
                  <Input
                    value={targeting[key] || ''}
                    onChange={e => updateTargeting(key, e.target.value)}
                    placeholder="Value"
                    disabled={isSaving || readOnlyTargetingKeys.includes(key)}
                    readOnly={readOnlyTargetingKeys.includes(key)}
                    className="flex-1"
                  />
                </div>
              ))}
              {/* Custom targeting keys */}
              {customKeys.map((key, index) => (
                <div key={`custom-${index}`} className="flex items-center gap-2">
                  <Input
                    value={key}
                    onChange={e => {
                      const currentTargeting = placement.targeting || {};
                      const value = currentTargeting[key] || '';
                      const updated = { ...currentTargeting };
                      delete updated[key];
                      updated[e.target.value] = value;
                      updateField('targeting', updated);
                    }}
                    placeholder="Key"
                    disabled={isSaving}
                    className="w-20"
                  />
                  <Input
                    value={targeting[key] || ''}
                    onChange={e => updateTargeting(key, e.target.value)}
                    placeholder="Value"
                    disabled={isSaving}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTargetingKey(key)}
                    disabled={isSaving}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
